/*
 * ------------------------------------------------------------------------------------------
 * - External Imports -
 * ------------------------------------------------------------------------------------------
 */
import { OnlinePatientManagementProvider } from '@ngt/opms';
import * as Sentry from "@sentry/react";
import { JsonServiceClient } from '@servicestack/client';
import { ISourceOptions } from "@tsparticles/engine";
import { createRoot } from 'react-dom/client';

/*
 * ------------------------------------------------------------------------------------------
 * - Internal Imports -
 * ------------------------------------------------------------------------------------------
 */

import { Box } from '@mui/material';
import { FC, lazy, StrictMode, Suspense, useCallback, useEffect } from 'react';
import { createBrowserRouter, createRoutesFromChildren, matchRoutes, RouteObject, useLocation, useNavigationType } from 'react-router-dom';
import { EventDefinitionCollectionResponse, FormDefinitionCollectionResponse, GetUser, GetUserResponse, IReturn, PatientStateCollectionResponse, TreatmentCollectionResponse } from './api/dtos';
import App from './App';
import { AppSettings } from './AppSettings';
import RouteErrorBoundary from './components/errors/RouteErrorBoundary';
import { createSentryAnonymousUser } from './errors/errors';
import createOnlinePatientManagement from './opms/onlinePatientManagement';

const Particles = lazy(() => import('@tsparticles/react'));

/*
 * ------------------------------------------------------------------------------------------
 * - Render -
 * ------------------------------------------------------------------------------------------
 */

declare let global: any;

if (typeof global === 'undefined') {
    (window as any).global = window;
}

if (AppSettings.sentry.dsn) {
    const integrations = [
        // React Router V6 integration.
        // Helps with tracing URL changes and which URLs belong to the 
        // same route, for example:
        //    - https://test.com/form/1
        //    - https://test.com/form/2
        // both belong to the same route */form/*
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        // Browser Tracing integration
        // Helps with tracing URLs and other common browser "spans"
        Sentry.browserTracingIntegration(),
        // Replay integration
        // Records user actions and sends to Sentry.
        Sentry.replayIntegration({
            // Whether to mask all text on the page when recording user actions
            // When false only inputs are masked.
            // There is a separate setting to force unmasked inputs.
            maskAllText: false,
            // Whether to mask all images on the page when recording user 
            // actions.
            blockAllMedia: false,
            // Which URLs can the replay run on.
            // URL must contain one of the strings in the array or match one of
            // the regexes supplied
            networkDetailAllowUrls: [window.location.origin],
            // Capture the body of network requests
            // Sentry will still run data scrubbing on the bodies.
            networkCaptureBodies: true
        })
    ];
    if (AppSettings.sentry.enableReportABug) {
        integrations.push(Sentry.feedbackIntegration({
            // Disable the injection of the default widget
            autoInject: false,
            colorScheme: 'system',
            showBranding: false,
            showName: false,
            showEmail: false,
            tags: {
                Application: AppSettings.sentry.application,
                Customer: AppSettings.sentry.customer
            },
            useSentryUser: {
                name: 'fullName',
                email: 'email',
            },
        }));
    }
    Sentry.init({
        // The DSN to use when sending information to Sentry.
        // Identifies a specific project in Sentry.
        dsn: AppSettings.sentry.dsn,
        // Whether to send identifying information to Sentry such as
        // username, useragent, ip, etc
        sendDefaultPii: AppSettings.sentry.sendDefaultPii,
        // Whether to sent available performance metrics to Sentry.
        // These are the basic performance metrics and not the endepth
        // profiling metrics that take a lot more effort to setup.
        enableMetrics: AppSettings.sentry.enableMetrics,
        // Whether to attach stacktraces to actions sent to Sentry.
        attachStacktrace: AppSettings.sentry.attachStacktrace,
        // Whether @sentry/react is in debug mode, logging more information
        // to the console.
        debug: AppSettings.sentry.debug,
        // initialise integrations.
        integrations: integrations,
        // The environment to associate all action sent to Sentry with.
        environment: AppSettings.sentry.environment,
        // Release to associate all actions sent to Sentry with.
        release: AppSettings.sentry.release,
        // Percentage of captured traces that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        tracesSampleRate: AppSettings.sentry.tracesSampleRate,
        // Which URLs to trace / record in a trace.
        // Only trace internal URLs don't want to trace every SEO url
        // under the sun.
        tracePropagationTargets: [
          'localhost', 
          /^\//,
          new RegExp(`/^${window.location.origin}`)
        ],
        // Percentage of captured session replays that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        replaysSessionSampleRate: AppSettings.sentry.replaysSessionSampleRate,
        // Percentage of captured session replays that are sent to Sentry.
        // when an error occurs
        // 1.0 = 100%, 0 = 0%
        replaysOnErrorSampleRate: AppSettings.sentry.replaysOnErrorSampleRate,
        // Enable logs to be sent to Sentry.
        enableLogs: AppSettings.sentry.enableLogs,
    });
    Sentry.setTag('Application', AppSettings.sentry.application);
    Sentry.setTag('Customer', AppSettings.sentry.customer);
    // Set user to anonymous at start up, this should be overriden by
    // React code if user information is available.
    Sentry.setUser(createSentryAnonymousUser());
}


const options: ISourceOptions = {
    background: {
        color: {
            value: "#eeeeee",
        },
    },
    fpsLimit: 120,
    interactivity: {
        events: {
            onClick: {
                enable: true,
                mode: "push",
            },
            onHover: {
                enable: true,
                mode: "repulse",
            },
        },
        modes: {
            push: {
                quantity: 4,
            },
            repulse: {
                distance: 200,
                duration: 0.4,
            },
        },
    },
    particles: {
        color: {
            value: [
                '#562c85',
                '#e8078c',
                '#76c6d7'
            ]
        },
        links: {
            color: "#cccccc",
            distance: 150,
            enable: true,
            opacity: 0.5,
            width: 1,
        },
        // collisions: {
        //     enable: true,
        //     mode: "bounce"
        // },
        move: {
            direction: "none",
            enable: true,
            outModes: {
                default: "bounce",
            },
            random: false,
            speed: 6,
            straight: false,
        },
        number: {
            density: {
                enable: true,
            },
            value: 80,
        },
        opacity: {
            value: 0.5,
        },
        shape: {
            type: "circle",
        },
        size: {
            value: { min: 1, max: 5 },
        },
    },
    detectRetina: true,
}



interface IAppContentProps {
    user?: GetUserResponse, 
    eventDefinitions?: EventDefinitionCollectionResponse, 
    formDefinitions?: FormDefinitionCollectionResponse, 
    patientStates?: PatientStateCollectionResponse, 
    treatments?: TreatmentCollectionResponse
}

const AppContent: FC<IAppContentProps> = ({
    user,
}) => {    
    const onlinePatientManagement = createOnlinePatientManagement(user);

    if (!onlinePatientManagement) {
        return null;
    }

    const createRouter = useCallback((routes: RouteObject[]) => Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)(routes), [createBrowserRouter]);


    if (AppSettings.particles) {
        return (
            <OnlinePatientManagementProvider 
                value={onlinePatientManagement}
                configuration={{
                    authenticatedUser: {
                        revalidateOnMount: false
                    },
                    eventDefinitions: {
                        revalidateOnMount: false
                    },
                    formDefinitions: {
                        revalidateOnMount: false
                    },
                    formMetadata: {
                        revalidateOnMount: false
                    },
                    patientStates: {
                        revalidateOnMount: false
                    },
                    treatments: {
                        revalidateOnMount: false
                    }
                }}
                createRouter={createRouter}
                ErrorBoundary={RouteErrorBoundary}
            >
                <Suspense fallback={<></>}>
                    <Particles 
                        id="tsParticles"
                        options={options}
                    />
                </Suspense>
                <Box sx={{ position: 'relative' }}>
                    <App />
                </Box>
            </OnlinePatientManagementProvider>   
        );
    }

    return (
        <OnlinePatientManagementProvider 
            value={onlinePatientManagement}
            configuration={{
                authenticatedUser: {
                    revalidateOnMount: false
                },
                eventDefinitions: {
                    revalidateOnMount: false
                },
                formDefinitions: {
                    revalidateOnMount: false
                },
                formMetadata: {
                    revalidateOnMount: false
                },
                patientStates: {
                    revalidateOnMount: false
                },
                treatments: {
                    revalidateOnMount: false
                }
            }}
        >
            <App />
        </OnlinePatientManagementProvider>
    )
}

const loadData = async <T,>(client: JsonServiceClient, request: IReturn<T>) => {
    try {
        return await client.get(request);
    }
    catch (error) {
        console.error(error);
    }

    return undefined;
}

const bootstrap = async () => {
    if (AppSettings.particles) {
        const { initParticlesEngine } = await import('@tsparticles/react');

        await initParticlesEngine(async (engine) => { 
            const { loadAll } = await import('@tsparticles/all');

            await loadAll(engine);
        })
    }


    if (AppSettings.debug) {
        const client = new JsonServiceClient();

        const [
            user, 
        ] = await Promise.all([
            loadData(client, new GetUser()),
        ]);

        return (
            <AppContent 
                user={user} 
            />
        );
    } else {
        const user = global.AUTH as GetUserResponse | undefined;

        return (
            <AppContent 
                user={user} 
            />
        );
    }
}


document.addEventListener('DOMContentLoaded', async function(_event) { 
    const appContent = await bootstrap();

    const root = AppSettings.sentry.dsn ? 
        createRoot(
            document.getElementById('root')!,
            {
                // Callback called when React automatically recovers from errors.
                onRecoverableError: Sentry.reactErrorHandler(),
            }
        ) :
        createRoot(document.getElementById('root')!);

    root.render(
        <StrictMode>
            <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
                {appContent}
            </Sentry.ErrorBoundary>
        </StrictMode>
    )
});